import { Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis, YAxis } from 'recharts'
import { cn } from '@/lib/utils'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart'

/**
 * Figma (node 449:7503 — Bar Chart):
 *
 * 9 variants:
 *   Basic       — Vertical bars, single series
 *   Horizontal  — Horizontal bars, single series
 *   Mixed       — Horizontal, multi-colored (uses fill from data)
 *   Custom Label — Horizontal with value labels
 *   Active      — One bar highlighted (activeIndex)
 *   Stacked     — Multiple series stacked
 *   Label       — Vertical with value labels on top
 *   Negative    — Bars with negative values
 *   Multiple    — Side-by-side grouped bars
 *
 * Specs:
 *   Bar: rounded-t-sm (2px) for vertical, rounded-r-sm for horizontal
 *   Colors: --chart-1 through --chart-5 via CSS variables
 *   Grid: horizontal dashed lines (vertical for horizontal layout)
 *   Axis: text-xs text-muted-foreground, tick line hidden
 */

export type BarChartLayout = 'vertical' | 'horizontal'

export interface IntuiBarChartProps {
  /** Chart data array */
  data: Record<string, unknown>[]
  /** Chart configuration (keys, labels, colors) */
  config: ChartConfig
  /** Data keys to render as bars (defaults to all config keys) */
  dataKeys?: string[]
  /** Category axis data key */
  categoryKey?: string
  /** Chart layout direction */
  layout?: BarChartLayout
  /** Stack bars on top of each other */
  stacked?: boolean
  /** Show value labels on bars */
  showLabels?: boolean
  /** Label position */
  labelPosition?: 'top' | 'right' | 'inside' | 'insideLeft'
  /** Active bar index (highlight one bar) */
  activeIndex?: number
  /** Use data-driven fill colors (for mixed variant) */
  useFillFromData?: boolean
  /** Fill key in data for per-bar colors */
  fillKey?: string
  /** Bar radius */
  radius?: number | [number, number, number, number]
  /** Show chart legend */
  showLegend?: boolean
  /** Show category axis */
  showCategoryAxis?: boolean
  /** Show value axis */
  showValueAxis?: boolean
  /** Show cartesian grid */
  showGrid?: boolean
  /** Show tooltip on hover */
  showTooltip?: boolean
  /** Bar size (width for vertical, height for horizontal) */
  barSize?: number
  /** Additional className for the container */
  className?: string
}

function IntuiBarChart({
  data,
  config,
  dataKeys,
  categoryKey = 'month',
  layout = 'vertical',
  stacked = false,
  showLabels = false,
  labelPosition = 'top',
  activeIndex,
  useFillFromData = false,
  fillKey = 'fill',
  radius = 4,
  showLegend = false,
  showCategoryAxis = true,
  showValueAxis = false,
  showGrid = true,
  showTooltip = true,
  barSize,
  className,
}: IntuiBarChartProps) {
  const keys = dataKeys ?? Object.keys(config)
  const stackId = stacked ? 'stack' : undefined
  const isHorizontal = layout === 'horizontal'

  return (
    <ChartContainer config={config} className={cn(className)}>
      <BarChart
        data={data}
        layout={isHorizontal ? 'vertical' : 'horizontal'}
        margin={
          isHorizontal
            ? { left: showValueAxis ? -20 : 0 }
            : { left: showValueAxis ? 12 : 0 }
        }
      >
        {showGrid && (
          <CartesianGrid vertical={isHorizontal} horizontal={!isHorizontal} />
        )}

        {isHorizontal ? (
          <>
            {showCategoryAxis && (
              <YAxis
                dataKey={categoryKey}
                type="category"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) =>
                  typeof value === 'string' ? value.slice(0, 3) : value
                }
              />
            )}
            {showValueAxis && (
              <XAxis type="number" hide={!showValueAxis} tickLine={false} axisLine={false} />
            )}
          </>
        ) : (
          <>
            {showCategoryAxis && (
              <XAxis
                dataKey={categoryKey}
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) =>
                  typeof value === 'string' ? value.slice(0, 3) : value
                }
              />
            )}
            {showValueAxis && (
              <YAxis tickLine={false} axisLine={false} tickMargin={8} />
            )}
          </>
        )}

        {showTooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel={keys.length === 1} />}
          />
        )}

        {keys.map((key) => (
          <Bar
            key={key}
            dataKey={key}
            fill={`var(--color-${key})`}
            radius={radius}
            stackId={stackId}
            barSize={barSize}
          >
            {activeIndex !== undefined
              ? data.map((_, index) => (
                  <Cell
                    key={`active-${index}`}
                    fillOpacity={index === activeIndex ? 1 : 0.3}
                  />
                ))
              : useFillFromData
                ? data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={(entry[fillKey] as string) || `var(--color-${key})`}
                    />
                  ))
                : null}
            {showLabels && (
              <LabelList
                dataKey={key}
                position={labelPosition}
                offset={8}
                className="fill-foreground"
                fontSize={12}
              />
            )}
          </Bar>
        ))}

        {/* `payload` is injected by recharts' Legend via cloneElement at render
            time (see recharts Legend.js), so the empty array here only satisfies
            the content-element prop type and is always overridden at runtime. */}
        {showLegend && <ChartLegend content={<ChartLegendContent payload={[]} />} />}
      </BarChart>
    </ChartContainer>
  )
}
IntuiBarChart.displayName = 'IntuiBarChart'

export { IntuiBarChart }
export type { ChartConfig }
